import { SeverityLevel } from '@microsoft/applicationinsights-common';
import {
    EventEmitter,
    FunctionalComponent,
    h,
} from '@stencil/core';

import { PortalServiceTileEvents } from '../../../models/appInsights/events/portal-service-tile.events';
import { SettingClickedEvent } from '../../../models/events/settingClickedEvent';
import { Service } from '../../../models/service';
import store from '../../../store/store';
import { Features } from '../../../utils/featureFlags/featureFlagConstants';
import { getFeatureFlag } from '../../../utils/featureFlags/featureFlags';
import { getLanguage } from '../../../utils/languageHelper';
import { t } from '../../../utils/localization/loc';
import Logger from '../../../utils/logger/logger';
import { trackStatsigEvent } from '../../../utils/logger/statsig';
import getSvg from '../../../utils/serviceIconHelper/serviceIconHelper';
import { getAccessToken } from '../../../utils/session/sessionStore';

export interface MoreTileMenuProps {
    open: boolean;
    top: number;
    services: Service[];
    onMoreMenuClose: (open?: boolean) => void;
    serviceClickedEvent: EventEmitter<SettingClickedEvent>;
}

export const MoreTileMenu: FunctionalComponent<MoreTileMenuProps> = ({
    open,
    top,
    services = [],
    onMoreMenuClose,
    serviceClickedEvent,
}) => {
    const enableLeftNavServiceRevamp = getFeatureFlag(Features.EnableLeftNavServiceRevamp.name);
    const filteredMoreServices = services?.filter(service => service.isMore);
    const tileAnchorRefs: Record<string, HTMLAnchorElement> = {};

    const getTile = (service) => (
        <a
            ref={(el: HTMLAnchorElement) => tileAnchorRefs[service.id] = el}
            href={service.url}
            aria-labelledby={`menu-text-${service.id}`}
            role='menuitem'
            id={'nav-menu-item-' + service.id}
            data-cy={'nav-menu-item-' + service.id}
            class="more-tile-menu-item"
            tabindex="-1"
            onClick={(_e) => {
                Logger.logCustomEvent(
                    PortalServiceTileEvents.ClickedService,
                    {
                        service: service.id,
                        location: 'more',
                    },
                    SeverityLevel.Verbose,
                );
                trackStatsigEvent(getAccessToken(), PortalServiceTileEvents.ClickedService, service.id, { location: 'more' });

                if (service.handled) {
                    _e.preventDefault();
                    serviceClickedEvent.emit({
                        serviceid: service.id,
                        url: service.url,
                    });
                }

                onMoreMenuClose();
            }}
            title={service.name}
        >
            <div
                onKeyUp={(ev: KeyboardEvent) => {
                    // FIXME: this has to be refactored, idk why this div has tabindex of 0 and the anchor -1.
                    if (ev.key === 'Enter' || ev.key === ' ') {
                        tileAnchorRefs[service.id].click();
                    }
                }}
                role="link"
                id={service.id}
                data-cy={'nav-menu-' + service.id}
                tabindex="0"
                class={`pt-menu-item-base${enableLeftNavServiceRevamp ? ' redesign' : ''}${service.active ? ' active' : ''}`}
            >
                <div class="pt-menu-item-icon">
                    <div data-cy={'nav-menu-icon-' + service.id} innerHTML={getSvg(service.svg)} class="pt-menu-item-icon"></div>
                </div>

                <div data-cy={'nav-menu-content-' + service.id} class='pt-menu-text-group pt-menu-item-active'>
                    <div class="menu-text" id={`menu-text-${service.id}`}>{service.name}</div>
                </div>
            </div>
        </a>
    );

    return (
        <div
            id="more-wrapper-root"
            data-cy="nav-menu-model"
            class={`pt-menu-wrapper ${open ? 'pt-visible' : ''}`}
            onClick={
                (ev) => {
                    if (ev.target === ev.currentTarget) {
                        onMoreMenuClose();
                    }
                }
            }
        >
            <div class="pt-modal" role="menu" style={{ top: `${top < 0 ? 0 : top}px` }}>
                { services.filter(service => !service.isMore).map(service => getTile(service)) }
                { enableLeftNavServiceRevamp && filteredMoreServices.length > 0 &&
                    <h4 class="more-products">{t('more_products', getLanguage(store))}</h4>
                }
                { enableLeftNavServiceRevamp && filteredMoreServices.map(service => getTile(service))}
            </div>
        </div>
    );
};

